import useGetMyTaxRates from '@/api/queries/me/useGetMyTaxRates';
import ConfirmDeleteModal from '@/ui/components/confirm-delete-modal/ConfirmDeleteModal';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  CurrencyInput,
  Divider,
  Field,
  Fieldset,
  Form,
  IncrementableNumberInput,
  Input,
  MarkdownTextarea,
  Select,
} from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import { useDebounceCallback } from '@payaca/hooks';
import { FC, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import {
  useOptimisticRemoveEstimateLineItem,
  useOptimisticUpdateEstimateLineItem,
} from '../api/mutations';
import { EstimateLineItem } from '../types';

const formSchema = z.object({
  internalReference: z.string(),
  description: z.string(),
  quantity: z.number().min(1),
  unitPriceExcTax: z.number().min(0),
  taxRateId: z.string().nullable(),
});
type FormValues = z.infer<typeof formSchema>;

const lineItemToFormValues = (lineItem: EstimateLineItem): FormValues => ({
  internalReference: lineItem.internalReference,
  description: lineItem.description ?? '',
  quantity: lineItem.quantity,
  unitPriceExcTax: lineItem.unitPriceExcTax.value,
  taxRateId: lineItem.taxRate?.publicId ?? null,
});

type EstimateItemEditorProps = {
  projectId: string;
  estimateId: string;
  lineItem: EstimateLineItem;
  onBack: () => void;
};

const EstimateItemEditor: FC<EstimateItemEditorProps> = (props) => {
  const { projectId, estimateId, lineItem, onBack } = props;

  /**
   * state
   */
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  /**
   * queries
   */
  const { taxRates } = useGetMyTaxRates();

  /**
   * mutations
   */
  const updateEstimateLineItem = useOptimisticUpdateEstimateLineItem({
    projectId,
    estimateId,
  });
  const removeEstimateLineItem = useOptimisticRemoveEstimateLineItem({
    projectId,
    estimateId,
  });

  /**
   * form state
   */
  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: lineItemToFormValues(lineItem),
  });
  const { control, subscribe } = methods;

  /**
   * START
   * subscribe to all form changes. and debounce mutations. (auto-save)
   */
  // Keep lastSavedRef in sync with the latest lineItem prop so that
  // server-normalised values (e.g. after invalidate-refetch) don't get
  // overwritten by the next debounced save.
  const lastSavedRef = useRef<FormValues>(lineItemToFormValues(lineItem));
  useEffect(() => {
    lastSavedRef.current = lineItemToFormValues(lineItem);
  }, [lineItem]);

  const debouncedSave = useDebounceCallback(
    (values: FormValues) => {
      const last = lastSavedRef.current;
      const hasChange = (Object.keys(values) as (keyof FormValues)[]).some(
        (key) => values[key] !== last[key]
      );
      if (!hasChange) return;
      lastSavedRef.current = values;
      updateEstimateLineItem.mutate({
        id: lineItem.id,
        internalReference: values.internalReference,
        description: values.description,
        quantity: values.quantity,
        unitPriceExcTax: values.unitPriceExcTax,
        taxRateId: values.taxRateId,
      });
    },
    500,
    { flushOnUnmount: true }
  );

  useEffect(() => {
    const unsubscribe = subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        debouncedSave(values);
      },
    });
    return () => {
      unsubscribe();
      debouncedSave.cancel();
    };
  }, [subscribe, debouncedSave]);
  /**
   * END
   */

  /**
   * callbacks
   */
  const handleConfirmDelete = () => {
    debouncedSave.cancel();
    removeEstimateLineItem.mutate({ id: lineItem.id });
    setIsConfirmingDelete(false);
  };

  /**
   * derived state
   */
  const taxRateOptions = (taxRates ?? []).map((rate) => ({
    label: rate.label,
    value: rate.publicId,
  }));

  return (
    <>
      <Form
        methods={methods}
        onSubmit={() => {}}
        className="typography:prose flex flex-col min-h-0 h-full"
      >
        <div className="p-3.5 pb-2.5">
          <Button onClick={onBack} variant="ghost" colour="grey" size="sm">
            Back to items
          </Button>
        </div>

        <div className="overflow-auto px-3.5 pb-4">
          <Fieldset>
            <div className="grid grid-cols-2 gap-3">
              <Field name="internalReference">
                <Field.Label>Reference</Field.Label>
                <Controller<FormValues, 'internalReference'>
                  control={control}
                  name="internalReference"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <Input value={value} onChange={onChange} onBlur={onBlur} />
                  )}
                />
              </Field>

              <Field name="quantity">
                <Field.Label>Quantity</Field.Label>
                <Controller<FormValues, 'quantity'>
                  control={control}
                  name="quantity"
                  render={({ field: { value, onChange } }) => (
                    <IncrementableNumberInput
                      value={value}
                      onChange={(next) => onChange(next ?? 0)}
                      min={1}
                    />
                  )}
                />
              </Field>
            </div>

            <Field name="description">
              <Field.Label>Description</Field.Label>
              <Controller<FormValues, 'description'>
                control={control}
                name="description"
                render={({ field: { value, onChange, onBlur } }) => (
                  <MarkdownTextarea
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder="Description…"
                  />
                )}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field name="unitPriceExcTax">
                <Field.Label>Price</Field.Label>
                <Controller<FormValues, 'unitPriceExcTax'>
                  control={control}
                  name="unitPriceExcTax"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <CurrencyInput
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      currency={{
                        code: lineItem.unitPriceExcTax.currency.code,
                      }}
                    />
                  )}
                />
              </Field>

              <Field name="taxRateId">
                <Field.Label>Tax rate</Field.Label>
                <Controller<FormValues, 'taxRateId'>
                  control={control}
                  name="taxRateId"
                  render={({ field: { value, onChange } }) => (
                    <Select
                      multiple={false}
                      options={taxRateOptions}
                      value={value ?? undefined}
                      onChange={(next) => onChange(next ?? null)}
                      placeholder="Select tax rate"
                    />
                  )}
                />
              </Field>
            </div>

            <Divider />

            <div className="flex items-center justify-between">
              <span className="supporting-body">Total (inc. tax)</span>
              <strong>
                <CurrencyValue {...lineItem.totalIncTax} />
              </strong>
            </div>
          </Fieldset>
        </div>

        <div className="px-3.5">
          <Button
            variant="dashed"
            colour="red"
            size="sm"
            className="w-full"
            onClick={() => setIsConfirmingDelete(true)}
          >
            Remove item
          </Button>
        </div>
      </Form>

      <ConfirmDeleteModal
        isOpen={isConfirmingDelete}
        onClose={() => setIsConfirmingDelete(false)}
        onConfirmDelete={handleConfirmDelete}
        isBusy={removeEstimateLineItem.isPending}
        entityName={{ singular: 'line item', plural: 'line items' }}
        destructiveVerb="remove"
      />
    </>
  );
};

export default EstimateItemEditor;
